import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiInvestmentGoalsData,
  type EnrollmentMultiInvestmentGoalsSubmitInput,
  type EnrollmentMultiInvestmentGoalsSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi InvestmentGoals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiInvestmentGoalsData>
 * @example const response = await getEnrollmentMultiInvestmentGoalsData()
 */
export const getEnrollmentMultiInvestmentGoalsData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiInvestmentGoalsData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiInvestmentGoalsData',
      query: `mutation getEnrollmentMultiInvestmentGoalsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              progress
              investmentProfile {
                  objective
                  experience
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiInvestmentGoalsData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiInvestmentGoalsData = createQueryHook<undefined, EnrollmentMultiInvestmentGoalsData>(
  getEnrollmentMultiInvestmentGoalsData,
)

/**
 * Submit Enrollment Multi InvestmentGoals data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiInvestmentGoalsSubmitOutput>
 * @example const response = await submitEnrollmentMultiInvestmentGoalsData();
 */
export const submitEnrollmentMultiInvestmentGoalsData = async (
  enrollmentInvestmentGoalsSubmitInput: EnrollmentMultiInvestmentGoalsSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiInvestmentGoalsSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiInvestmentGoalsData',
      query: `mutation submitEnrollmentMultiInvestmentGoalsData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentInvestmentGoalsSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiInvestmentGoalsSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiInvestmentGoalsData = createMutationHook<
  EnrollmentMultiInvestmentGoalsSubmitInput,
  EnrollmentMultiInvestmentGoalsSubmitOutput
>(submitEnrollmentMultiInvestmentGoalsData)
